import { useState, Fragment } from 'react';
import { m, AnimatePresence } from 'motion/react';

import { Picture } from './Picture';

const PARQUET_LIGHT = '#e8dcc6';
const PARQUET_DARK  = '#c9a982';
const TILE_LIGHT    = '#e5e7eb';
const WALL          = '#1f2937';
const FURNITURE     = '#4b5563';
const FURNITURE_LIGHT = '#9ca3af';
const TEXTILE       = '#a78b6f';
const COUNTER       = '#6b7280';

type FloorType = 'parquet' | 'tile' | 'grass';
type Room = { x: number; y: number; w: number; h: number; label: string; area?: string; floor?: FloorType };

function Bed({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#f3f4f6" stroke={FURNITURE} strokeWidth="0.25" rx="0.6" />
      <rect x={x + 0.6} y={y + 0.6} width={w - 1.2} height={h * 0.25} fill={TEXTILE} rx="0.4" />
      <rect x={x + w * 0.15} y={y + h * 0.05} width={w * 0.32} height={h * 0.18} fill="#fff" stroke={FURNITURE_LIGHT} strokeWidth="0.15" rx="0.3" />
      <rect x={x + w * 0.53} y={y + h * 0.05} width={w * 0.32} height={h * 0.18} fill="#fff" stroke={FURNITURE_LIGHT} strokeWidth="0.15" rx="0.3" />
    </g>
  );
}

function SingleBed({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#f3f4f6" stroke={FURNITURE} strokeWidth="0.25" rx="0.4" />
      <rect x={x + 0.4} y={y + 0.4} width={w - 0.8} height={h * 0.22} fill={TEXTILE} rx="0.3" />
      <rect x={x + w * 0.2} y={y + h * 0.05} width={w * 0.6} height={h * 0.15} fill="#fff" stroke={FURNITURE_LIGHT} strokeWidth="0.15" rx="0.2" />
    </g>
  );
}

function Sofa({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={TEXTILE} stroke={FURNITURE} strokeWidth="0.25" rx="0.8" />
      <rect x={x + 0.6} y={y + h * 0.3} width={w - 1.2} height={h * 0.5} fill="#c4ad8e" rx="0.4" />
      <rect x={x + 0.4} y={y + 0.4} width={w * 0.3} height={h * 0.3} fill="#dac9ad" rx="0.3" />
      <rect x={x + w - w * 0.3 - 0.4} y={y + 0.4} width={w * 0.3} height={h * 0.3} fill="#dac9ad" rx="0.3" />
    </g>
  );
}

function CoffeeTable({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return <rect x={x} y={y} width={w} height={h} fill="#3f3a36" stroke={FURNITURE} strokeWidth="0.2" rx="0.4" />;
}

function TVUnit({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#1f2937" stroke={FURNITURE} strokeWidth="0.2" rx="0.2" />
      <rect x={x + w * 0.1} y={y + h * 0.15} width={w * 0.8} height={h * 0.55} fill="#0f172a" rx="0.2" />
    </g>
  );
}

function DiningTable({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} fill="#3f3a36" stroke={FURNITURE} strokeWidth="0.2" />
      <circle cx={x + w / 2} cy={y - 1} r="1.2" fill={FURNITURE_LIGHT} />
      <circle cx={x + w / 2} cy={y + h + 1} r="1.2" fill={FURNITURE_LIGHT} />
      <circle cx={x - 1} cy={y + h / 2} r="1.2" fill={FURNITURE_LIGHT} />
      <circle cx={x + w + 1} cy={y + h / 2} r="1.2" fill={FURNITURE_LIGHT} />
    </g>
  );
}

function Kitchen({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={1.6} fill={COUNTER} stroke={FURNITURE} strokeWidth="0.2" />
      <rect x={x} y={y} width={1.6} height={h} fill={COUNTER} stroke={FURNITURE} strokeWidth="0.2" />
      <rect x={x + w * 0.2} y={y + 0.2} width={w * 0.18} height={1.2} fill="#cbd5e1" stroke={FURNITURE_LIGHT} strokeWidth="0.1" rx="0.2" />
      <rect x={x + w * 0.55} y={y + 0.2} width={w * 0.22} height={1.2} fill="#1f2937" rx="0.1" />
      <circle cx={x + w * 0.6} cy={y + 0.8} r="0.3" fill="#475569" />
      <circle cx={x + w * 0.7} cy={y + 0.8} r="0.3" fill="#475569" />
    </g>
  );
}

function Bath({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x + 0.4} y={y + 0.4} width="2" height="2.6" fill="#f3f4f6" stroke={FURNITURE_LIGHT} strokeWidth="0.15" rx="0.6" />
      <rect x={x + 0.4} y={y + h - 2} width="2.4" height="1.4" fill="#f3f4f6" stroke={FURNITURE_LIGHT} strokeWidth="0.15" rx="0.3" />
      <rect x={x + w - 3.5} y={y + 0.4} width="3" height={h - 0.8} fill="#dbeafe" fillOpacity="0.5" stroke={FURNITURE_LIGHT} strokeWidth="0.2" strokeDasharray="0.6 0.4" rx="0.2" />
      <circle cx={x + w - 2} cy={y + 1.5} r="0.4" fill={FURNITURE} />
    </g>
  );
}

function Wardrobe({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#a8a29e" stroke={FURNITURE} strokeWidth="0.2" />
      {Array.from({ length: Math.floor(w / 1.5) }).map((_, i) => (
        <line key={i} x1={x + (i + 1) * 1.5} y1={y} x2={x + (i + 1) * 1.5} y2={y + h} stroke={FURNITURE} strokeWidth="0.15" />
      ))}
    </g>
  );
}

function Plan({ rooms, furniture, floorDividerY }: { rooms: Room[]; furniture: React.ReactNode; floorDividerY?: number }) {
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="parquet" width="6" height="2" patternUnits="userSpaceOnUse">
          <rect width="6" height="2" fill={PARQUET_LIGHT} />
          <rect x="0" y="0" width="3" height="2" fill={PARQUET_DARK} fillOpacity="0.18" />
          <line x1="3" y1="0" x2="3" y2="2" stroke={PARQUET_DARK} strokeOpacity="0.3" strokeWidth="0.08" />
          <line x1="0" y1="0" x2="6" y2="0" stroke={PARQUET_DARK} strokeOpacity="0.2" strokeWidth="0.05" />
        </pattern>
        <pattern id="tile" width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="4" fill={TILE_LIGHT} />
          <line x1="0" y1="0" x2="4" y2="0" stroke="#cbd5e1" strokeWidth="0.1" />
          <line x1="0" y1="0" x2="0" y2="4" stroke="#cbd5e1" strokeWidth="0.1" />
        </pattern>
      </defs>

      <rect x="2" y="2" width="96" height="96" fill="#f5f5f4" />

      {rooms.map((r, i) => (
        <rect
          key={`floor-${i}`}
          x={r.x} y={r.y} width={r.w} height={r.h}
          fill={
            r.floor === 'tile'  ? 'url(#tile)'    :
            r.floor === 'grass' ? '#bbf7d0'        :
            'url(#parquet)'
          }
          fillOpacity={r.floor === 'grass' ? 0.7 : 1}
        />
      ))}

      {rooms.map((r, i) => (
        <rect key={`wall-${i}`} x={r.x} y={r.y} width={r.w} height={r.h}
          fill="none" stroke={WALL} strokeWidth="0.6" />
      ))}

      <rect x="2" y="2" width="96" height="96" fill="none" stroke={WALL} strokeWidth="1.4" />

      {furniture}

      {/* Floor divider for duplex */}
      {floorDividerY && (
        <g>
          <line x1="2" y1={floorDividerY} x2="98" y2={floorDividerY} stroke={WALL} strokeWidth="1.2" strokeDasharray="3 1.5" />
          <rect x="28" y={floorDividerY - 3} width="18" height="6" fill="#1e293b" rx="1" />
          <text x="37" y={floorDividerY + 0.6} fontSize="2.2" fill="white" textAnchor="middle" dominantBaseline="middle" fontFamily="system-ui,sans-serif" fontWeight="700">ALT KAT</text>
          <rect x="54" y={floorDividerY - 3} width="18" height="6" fill="#1e293b" rx="1" />
          <text x="63" y={floorDividerY + 0.6} fontSize="2.2" fill="white" textAnchor="middle" dominantBaseline="middle" fontFamily="system-ui,sans-serif" fontWeight="700">ÜST KAT</text>
        </g>
      )}

      {rooms.map((r, i) => (
        <g key={`label-${i}`}>
          <text x={r.x + r.w / 2} y={r.y + r.h / 2 - (r.area ? 1.4 : 0)}
            fontSize="2.4" fill="#1f2937" textAnchor="middle" dominantBaseline="middle"
            fontFamily="system-ui,sans-serif" fontWeight="700" letterSpacing="0.1">
            {r.label}
          </text>
          {r.area && (
            <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 2.2}
              fontSize="1.8" fill="#4b5563" textAnchor="middle" dominantBaseline="middle"
              fontFamily="system-ui,sans-serif">
              {r.area}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

/* ── 4+1 ── */
const plan4plus1: Room[] = [
  { label: 'SALON',      area: '30m²', x: 4,  y: 4,  w: 46, h: 34, floor: 'parquet' },
  { label: 'MUTFAK',     area: '12m²', x: 4,  y: 38, w: 26, h: 22, floor: 'tile'    },
  { label: 'YEM. ODASI', area: '10m²', x: 30, y: 38, w: 20, h: 22, floor: 'parquet' },
  { label: 'GİRİŞ',     area: '5m²',  x: 4,  y: 70, w: 46, h: 16, floor: 'tile'    },
  { label: 'YATAK 1',   area: '18m²', x: 50, y: 4,  w: 46, h: 20, floor: 'parquet' },
  { label: 'EBV. BANYO',area: '5m²',  x: 72, y: 24, w: 24, h: 14, floor: 'tile'    },
  { label: 'YATAK 2',   area: '13m²', x: 50, y: 24, w: 22, h: 14, floor: 'parquet' },
  { label: 'YATAK 3',   area: '13m²', x: 50, y: 38, w: 22, h: 16, floor: 'parquet' },
  { label: 'YATAK 4',   area: '11m²', x: 50, y: 54, w: 22, h: 16, floor: 'parquet' },
  { label: 'BANYO',     area: '4m²',  x: 72, y: 38, w: 24, h: 16, floor: 'tile'    },
  { label: 'WC',        area: '3m²',  x: 72, y: 54, w: 24, h: 16, floor: 'tile'    },
  { label: 'HOL',       area: '6m²',  x: 50, y: 70, w: 46, h: 16, floor: 'parquet' },
  { label: 'BALKON',    area: '10m²', x: 4,  y: 86, w: 92, h: 10, floor: 'tile'    },
];
const furniture4plus1 = (
  <g>
    <Sofa x={7} y={8} w={22} h={7} />
    <Sofa x={7} y={20} w={14} h={6} />
    <CoffeeTable x={12} y={28} w={12} h={4} />
    <TVUnit x={30} y={6} w={18} h={3} />
    <Kitchen x={6} y={40} w={22} h={18} />
    <DiningTable x={32} y={40} w={14} h={10} />
    <Bed x={56} y={6} w={22} h={12} />
    <Wardrobe x={82} y={6} w={12} h={14} />
    <Bath x={73} y={25} w={22} h={12} />
    <SingleBed x={54} y={27} w={16} h={9} />
    <SingleBed x={54} y={41} w={16} h={10} />
    <SingleBed x={54} y={57} w={16} h={10} />
    <Bath x={73} y={39} w={22} h={14} />
    <Bath x={73} y={55} w={22} h={14} />
  </g>
);

/* ── Dubleks ── */
const planDubleks: Room[] = [
  { label: 'SALON',      area: '32m²', x: 4,  y: 4,  w: 52, h: 28, floor: 'parquet' },
  { label: 'MUTFAK',     area: '12m²', x: 4,  y: 32, w: 28, h: 16, floor: 'tile'    },
  { label: 'YEM. ODASI', area: '10m²', x: 32, y: 32, w: 24, h: 16, floor: 'parquet' },
  { label: 'GİRİŞ',     area: '5m²',  x: 56, y: 4,  w: 40, h: 14, floor: 'tile'    },
  { label: 'MİS. WC',   area: '3m²',  x: 56, y: 18, w: 20, h: 12, floor: 'tile'    },
  { label: 'ÇAMAŞIR',   area: '4m²',  x: 76, y: 18, w: 20, h: 12, floor: 'tile'    },
  { label: 'MERDİVEN',  area: '',     x: 56, y: 30, w: 40, h: 18, floor: 'tile'    },
  { label: 'YATAK 1',   area: '18m²', x: 4,  y: 52, w: 38, h: 26, floor: 'parquet' },
  { label: 'EBV. BANYO',area: '5m²',  x: 4,  y: 78, w: 22, h: 18, floor: 'tile'    },
  { label: 'GİYİNME',   area: '4m²',  x: 26, y: 78, w: 16, h: 18, floor: 'parquet' },
  { label: 'YATAK 2',   area: '14m²', x: 42, y: 52, w: 30, h: 26, floor: 'parquet' },
  { label: 'YATAK 3',   area: '13m²', x: 72, y: 52, w: 24, h: 26, floor: 'parquet' },
  { label: 'BANYO',     area: '4m²',  x: 42, y: 78, w: 30, h: 18, floor: 'tile'    },
  { label: 'HOL',       area: '5m²',  x: 72, y: 78, w: 24, h: 18, floor: 'parquet' },
];
const furnitureDubleks = (
  <g>
    <Sofa x={6} y={6} w={22} h={7} />
    <CoffeeTable x={10} y={16} w={12} h={4} />
    <TVUnit x={30} y={6} w={18} h={3} />
    <Kitchen x={6} y={34} w={24} h={12} />
    <DiningTable x={32} y={35} w={16} h={10} />
    <Bed x={8} y={56} w={22} h={14} />
    <Wardrobe x={6} y={80} w={18} h={3} />
    <Bath x={5} y={79} w={20} h={16} />
    <SingleBed x={46} y={56} w={18} h={12} />
    <SingleBed x={76} y={56} w={16} h={12} />
    <Bath x={43} y={79} w={28} h={16} />
  </g>
);

/* ── Bahçe Loft Dubleks ── */
const planBahceLoft: Room[] = [
  { label: 'SALON / LOFT', area: '34m²', x: 4,  y: 4,  w: 58, h: 30, floor: 'parquet' },
  { label: 'MUTFAK',       area: '11m²', x: 4,  y: 34, w: 32, h: 10, floor: 'tile'    },
  { label: 'BANYO',        area: '4m²',  x: 36, y: 34, w: 26, h: 10, floor: 'tile'    },
  { label: 'GİRİŞ',       area: '5m²',  x: 62, y: 4,  w: 34, h: 18, floor: 'tile'    },
  { label: 'HOL',          area: '5m²',  x: 62, y: 22, w: 34, h: 22, floor: 'parquet' },
  { label: 'LOFT YATAK',  area: '22m²', x: 4,  y: 48, w: 58, h: 28, floor: 'parquet' },
  { label: 'YATAK 2',     area: '13m²', x: 62, y: 48, w: 34, h: 18, floor: 'parquet' },
  { label: 'EBV. BANYO',  area: '5m²',  x: 62, y: 66, w: 34, h: 10, floor: 'tile'    },
  { label: 'ÖZEL BAHÇE',  area: '40m²', x: 4,  y: 80, w: 92, h: 16, floor: 'grass'   },
];
const furnitureBahceLoft = (
  <g>
    <Sofa x={6} y={6} w={24} h={7} />
    <CoffeeTable x={10} y={16} w={14} h={4} />
    <TVUnit x={36} y={6} w={20} h={3} />
    <Kitchen x={6} y={35} w={28} h={8} />
    <Bath x={37} y={35} w={24} h={8} />
    <Bed x={8} y={52} w={24} h={14} />
    <Wardrobe x={36} y={50} w={22} h={3} />
    <SingleBed x={66} y={52} w={18} h={12} />
    <Bath x={63} y={67} w={32} h={8} />
    {/* Bahçe decoration */}
    <circle cx="20" cy="88" r="3.5" fill="#16a34a" fillOpacity="0.5" />
    <circle cx="50" cy="86" r="4" fill="#16a34a" fillOpacity="0.4" />
    <circle cx="80" cy="88" r="3" fill="#16a34a" fillOpacity="0.45" />
  </g>
);

const planLayouts: Record<string, { rooms: Room[]; furniture: React.ReactNode; floorDividerY?: number }> = {
  '4+1':              { rooms: plan4plus1,   furniture: furniture4plus1  },
  'Dubleks':          { rooms: planDubleks,  furniture: furnitureDubleks,  floorDividerY: 50 },
  'Bahçe Loft Dubleks': { rooms: planBahceLoft, furniture: furnitureBahceLoft, floorDividerY: 46 },
};

const slidePhotos: Record<string, { media: string; label: string }[]> = {
  '2+1': [
    { media: 'plans/floor-plan-2plus1',       label: '2+1' },
    { media: 'plans/floor-plan-2plus1-bblok', label: '2+1 B Blok' },
  ],
  '3+1': [
    { media: 'plans/floor-plan-3plus1-main',  label: '3+1' },
    { media: 'plans/floor-plan-3plus1-ablok', label: '3+1 A Blok' },
  ],
};

const TABS = ['2+1', '3+1', '4+1', 'Dubleks', 'Bahçe Loft Dubleks'];

export function FloorPlans() {
  const [activeTab, setActiveTab] = useState('2+1');
  const [slideIndex, setSlideIndex] = useState(0);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSlideIndex(0);
  };

  const slides = slidePhotos[activeTab];
  const layout = planLayouts[activeTab];

  return (
    <section id="floorplans" className="py-24 bg-gray-50 dark:bg-slate-900">
      <div className="container mx-auto px-4 lg:px-8">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full text-sm tracking-wider mb-4">
            KAT PLANLARI
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            Size Uygun{' '}
            <span className="text-stone-700 dark:text-stone-300">Planı Seçin</span>
          </h2>
        </m.div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="flex flex-wrap justify-center gap-2 bg-white dark:bg-slate-950 rounded-xl p-2 shadow-lg">
            {TABS.map((tab, i) => (
              // Fragment dizinin elemanı olduğu için key ONUN üzerinde olmalı;
              // içerideki key'ler saymıyor (SSR bunu uyarı olarak yakaladı).
              <Fragment key={tab}>
                {i === 3 && <div className="w-full md:hidden" />}
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    activeTab === tab
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-md'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {tab}
                </button>
              </Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <m.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white dark:bg-slate-950 rounded-2xl shadow-xl overflow-hidden">
              <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center p-4">

                {slides ? (
                  <>
                    {/* Fotoğraf */}
                    <AnimatePresence mode="wait">
                      <m.div
                        key={slideIndex}
                        className="w-full"
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -40 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Picture
                          media={slides[slideIndex].media}
                          alt={`${slides[slideIndex].label} daire kat planı`}
                          sizes="(min-width: 896px) 896px, 100vw"
                          width={896}
                          className="w-full h-auto object-contain rounded-lg"
                        />
                      </m.div>
                    </AnimatePresence>

                    {/* Ok butonları */}
                    {slideIndex > 0 && (
                      <button
                        onClick={() => setSlideIndex(slideIndex - 1)}
                        className="absolute left-6 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 dark:bg-slate-700/80 backdrop-blur shadow flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 transition-all"
                        aria-label="Önceki"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    )}
                    {slideIndex < slides.length - 1 && (
                      <button
                        onClick={() => setSlideIndex(slideIndex + 1)}
                        className="absolute right-6 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 dark:bg-slate-700/80 backdrop-blur shadow flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 transition-all"
                        aria-label="Sonraki"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    )}

                    {/* Dot göstergesi + etiket */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                      <span className="text-xs font-medium text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-slate-800/80 backdrop-blur px-3 py-1 rounded-full">
                        {slides[slideIndex].label}
                      </span>
                      <div className="flex">
                        {slides.map((s, i) => (
                          <button
                            key={s.media}
                            type="button"
                            onClick={() => setSlideIndex(i)}
                            aria-label={`${s.label} kat planını göster`}
                            aria-current={i === slideIndex}
                            className="flex items-center justify-center w-6 h-6"
                          >
                            <span
                              aria-hidden="true"
                              className={`block w-2 h-2 rounded-full transition-all duration-200 ${
                                i === slideIndex ? 'bg-slate-800 dark:bg-white scale-125' : 'bg-slate-400 dark:bg-slate-500'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="w-full aspect-square">
                    {layout ? (
                      <Plan rooms={layout.rooms} furniture={layout.furniture} floorDividerY={layout.floorDividerY} />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </m.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
